import React from 'react';
import { createRoot } from 'react-dom/client';
import './AppSettings/routes/index.css';
import './theme/tokens.css';
import App from './AppSettings/routes/App';
import reportWebVitals from './AppSettings/routes/reportWebVitals'; // Keep this import
import WebFontLoader from 'webfontloader';
import 'font-awesome/css/font-awesome.min.css';
import '@fortawesome/fontawesome-free/css/all.min.css';

import { BrowserRouter as Router } from 'react-router-dom';

// MSAL imports
import {
  PublicClientApplication,
  EventType,
  type AuthenticationResult,
  type AccountInfo,
  type EventMessage,
} from '@azure/msal-browser';
import { msalConfig } from './AppSettings/routes/config/authConfig';
import { ErrorProvider } from './Context/ErrorContext';

export let msalInstance: any;

msalInstance = new PublicClientApplication(msalConfig as any);

msalInstance.initialize().then(() => {
  // Initialization complete
  if (
    !msalInstance.getActiveAccount() &&
    msalInstance.getAllAccounts().length > 0
  ) {
    msalInstance.setActiveAccount(msalInstance.getAllAccounts()[0]);
  }

  // E2E test seam: provide a synthetic signed-in account so components that read
  // msalInstance.getActiveAccount()/getAllAccounts() work without a real Azure AD
  // login. Guarded by REACT_APP_E2E; completely inert in normal and production builds.
  if (process.env.REACT_APP_E2E === 'true') {
    // `oid` drives useActiveEmployeeId -> useCurrentEmployee. In mocked runs any value
    // works (the employee lookup is stubbed); in full-stack-UI runs it must match a seeded
    // employee row, so it's overridable via REACT_APP_E2E_OID. The localStorage override
    // (E2E_OID) lets Playwright switch personas per-test without rebooting the dev server —
    // same guarded-seam pattern as E2E_ACCESS_TOKEN in the axios interceptor.
    let storedOid: string | null = null;
    try {
      storedOid =
        typeof window !== 'undefined'
          ? window.localStorage.getItem('E2E_OID')
          : null;
    } catch {
      /* localStorage unavailable; fall through to env */
    }
    const e2eOid =
      storedOid || process.env.REACT_APP_E2E_OID || 'e2e-local-account-id';

    // local-dev self-mint: when a real tenant is configured (REACT_APP_E2E_TID, set by
    // scripts/local), mint an unsigned JWT carrying the seeded tenant/user claims and prime
    // it as the access token. The backend's E2E_AUTH_BYPASS path decodes it WITHOUT verifying
    // the signature, so the placeholder signature segment is fine. The interceptor then just
    // returns this injected token. Only set when absent so a Playwright-primed token wins.
    try {
      if (
        process.env.REACT_APP_E2E_TID &&
        typeof window !== 'undefined' &&
        !window.localStorage.getItem('E2E_ACCESS_TOKEN')
      ) {
        const b64url = (obj: unknown) =>
          btoa(JSON.stringify(obj))
            .replace(/\+/g, '-')
            .replace(/\//g, '_')
            .replace(/=+$/, '');
        const role = process.env.REACT_APP_E2E_ROLE || 'System Administrator';
        const header = { alg: 'HS256', typ: 'JWT' };
        const payload = {
          tid: process.env.REACT_APP_E2E_TID,
          oid: e2eOid,
          sub: e2eOid,
          roles: [role],
          role,
          preferred_username: 'e2e.user@example.com',
        };
        const token = `${b64url(header)}.${b64url(payload)}.e2e`;
        window.localStorage.setItem('E2E_ACCESS_TOKEN', token);
      }
    } catch {
      // best-effort; mocked Playwright runs don't need a real token
    }

    const e2eAccount: AccountInfo = {
      homeAccountId: 'e2e-home-account-id',
      localAccountId: e2eOid,
      environment: 'login.microsoftonline.com',
      tenantId: process.env.REACT_APP_AZURE_TENANT_ID || 'e2e-tenant',
      username: 'e2e.user@example.com',
      name: 'E2E Test User',
      idTokenClaims: { oid: e2eOid },
    };
    try {
      if (msalInstance.getAllAccounts().length === 0) {
        msalInstance.getAllAccounts = () => [e2eAccount];
      }
      msalInstance.setActiveAccount(e2eAccount);
    } catch {
      // best-effort; the <AuthGate> bypass in App.js still renders the app
    }
  }

  msalInstance.enableAccountStorageEvents();

  msalInstance.addEventCallback((event: EventMessage) => {
    const { eventType, payload } = event;

    const authenticationResult = payload as AuthenticationResult | undefined;
    const accountPayload = payload as AccountInfo | undefined;

    switch (eventType) {
      case EventType.LOGIN_SUCCESS:
      case EventType.SSO_SILENT_SUCCESS:
      case EventType.ACQUIRE_TOKEN_SUCCESS: {
        const account = authenticationResult?.account;
        if (account) {
          msalInstance.setActiveAccount(account);
        }
        break;
      }
      case EventType.ACCOUNT_ADDED: {
        if (accountPayload) {
          msalInstance.setActiveAccount(accountPayload);
        }
        break;
      }
      case EventType.ACCOUNT_REMOVED: {
        if (!accountPayload) {
          msalInstance.setActiveAccount(null);
          break;
        }
        const activeAccount = msalInstance.getActiveAccount();
        if (
          activeAccount &&
          activeAccount.homeAccountId === accountPayload.homeAccountId
        ) {
          const [nextAccount] = msalInstance.getAllAccounts();
          msalInstance.setActiveAccount(nextAccount ?? null);
        }
        break;
      }
      case EventType.LOGOUT_SUCCESS: {
        msalInstance.setActiveAccount(null);
        break;
      }
      default:
        break;
    }
  });

  // Global error handlers to capture unhandled JavaScript errors
  window.addEventListener('error', (event) => {
    console.error('Unhandled JavaScript error:', event.error);

    // Try to get the error context from React if available
    const errorContext = (window as any).ReactErrorContext;
    if (errorContext && errorContext.setError) {
      const errorMessage =
        event.error?.message || 'An unexpected error occurred';
      errorContext.setError(errorMessage);
    }
  });

  // Global handler for unhandled promise rejections
  window.addEventListener('unhandledrejection', (event) => {
    console.error('Unhandled promise rejection:', event.reason);

    // Try to get the error context from React if available
    const errorContext = (window as any).ReactErrorContext;
    if (errorContext && errorContext.setError) {
      const errorMessage =
        event.reason?.message || event.reason || 'An unexpected error occurred';
      errorContext.setError(errorMessage);
    }

    // Prevent the default console error
    event.preventDefault();
  });

  // Guarded: this entry module is IMPORTED by service code (axiosInterceptors reads
  // msalInstance from here), so it executes inside jest/jsdom where no #root exists —
  // rendering must only happen where the page provides the container. Production always
  // does (public/index.html); a missing container anywhere else is a no-op, not a crash.
  const container = document.getElementById('root');
  if (container) {
    const root = createRoot(container);

    root.render(
      <Router>
        {/* <ThemeProvider theme={theme}> */}
        <ErrorProvider>
          <App pca={msalInstance} />
        </ErrorProvider>
        {/* </ThemeProvider> */}
      </Router>
    );
  }
});

WebFontLoader.load({
  google: {
    families: ['Raleway:400,700'], // Specify the font and its weights
  },
});

// Measure performance and log results (you can replace `console.log` with any other function)
reportWebVitals(console.log);

// }
